import { useAuth } from '@clerk/nextjs';
import { setTelemetryIdentity } from '@firmable/telemetry/browser';
import React from 'react';

/**
 * Binds the Clerk user and organization ids to browser spans.
 *
 * Deliberately id-only. The Logfire browser SDK's default `getUser()` would
 * send name and email; Firmable's strict-PII rule forbids both, so the
 * telemetry package refuses anything else and this is the only input it takes.
 * An org id is an identifier rather than personal data, which is why it belongs
 * here while the org's name and domain do not.
 *
 * The org id matters as much as the user id, and was the half that was missing:
 * a browser error you cannot attribute to a customer cannot be prioritised, and
 * "which orgs are affected" is the first question asked about every one of them.
 *
 * Separate from the PostHog components on purpose — PostHog owns product
 * analytics and is untouched by this. Correlate the two after the fact by
 * joining on the Clerk user id, which both systems already carry.
 */
function UserBinding() {
  const { userId, orgId, isSignedIn } = useAuth();

  React.useEffect(() => {
    if (!isSignedIn || !userId) {
      setTelemetryIdentity({});
      return;
    }

    setTelemetryIdentity({ userId, orgId: orgId ?? undefined });

    // Signing out must clear both, or the next visitor inherits this identity.
    return () => setTelemetryIdentity({});
  }, [isSignedIn, userId, orgId]);

  return null;
}

export { UserBinding };
